import { communicationWorkflowApi } from "./api";
import {
  CreateWorkflowRequest,
  UpdateWorkflowTriggerRequest,
  UpdateWorkflowActionsRequest,
  WorkflowFilters,
  CloneWorkflowRequest,
  WorkflowsManagerProps,
  WorkflowRunFilters,
} from "./types";
import { toast } from "@shared/hooks/use-toast";
import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useQueryClient } from "@tanstack/react-query";

export const useCommunicationWorkflows = (
  filters: WorkflowFilters = {
    page: 0,
    limit: 0,
  }
) => {
  const utilityId = useRemoteUtilityId();
  const key = QueryKeyFactory.module.ux.workflow.workflowTable(filters);

  const enabled = !!utilityId;

  return useSmartQuery(
    key as unknown[],
    () => communicationWorkflowApi.getWorkflows(utilityId, filters),
    { enabled }
  );
};

export const useWorkflow = (workflowId?: number) => {
  const utilityId = useRemoteUtilityId();
  const key = QueryKeyFactory.module.ux.workflow.workflowById(workflowId);

  return useSmartQuery(
    key as unknown[],
    async () => {
      return communicationWorkflowApi.getWorkflowById(workflowId, utilityId);
    },
    {
      enabled: !!workflowId && !!utilityId, // Added && !!utilityId check
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useCreateWorkflow = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (workflowData: Omit<CreateWorkflowRequest, "remoteUtilityId">) => {
      const payload: CreateWorkflowRequest = {
        remoteUtilityId: utilityId,
        ...workflowData,
      };

      return communicationWorkflowApi.createWorkflow(payload);
    },
    {
      onSuccess: (data, variable) => {
        queryClient.invalidateQueries({
          queryKey: ["ux", "uxWorkflow", "list"],
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.communication.workflowsAll(),
        });
      },
    }
  );
};
export const usePatchCreateWorkflow = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async ({
      workflowId,
      workflowData,
    }: {
      workflowId: number;
      workflowData: Omit<CreateWorkflowRequest, "remoteUtilityId">;
    }) => {
      const payload: CreateWorkflowRequest = {
        remoteUtilityId: utilityId,
        ...workflowData,
      };

      return communicationWorkflowApi.patchCreatedWorkflow(workflowId, payload);
    },
    {
      onSuccess: (data, variable) => {
        queryClient.invalidateQueries({
          queryKey: ["ux", "uxWorkflow", "list"],
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.communication.workflowsAll(),
        });
      },
    }
  );
};

export const useUpdateWorkflowTrigger = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async ({
      workflowId,
      triggerData,
    }: {
      workflowId: number;
      triggerData: UpdateWorkflowTriggerRequest;
    }) => {
      return communicationWorkflowApi.updateWorkflowTrigger(
        workflowId,
        triggerData
      );
    },
    {
      onSuccess: (data, variable) => {
        queryClient.invalidateQueries({
          queryKey: ["ux", "uxWorkflow", "list"],
        });
      },
    }
  );
};

export const useUpdateWorkflowActions = () => {
  const queryKey = QueryKeyFactory.module.ux.workflow.workflowTable();
  const queryClient = useQueryClient();
  return useSmartMutation(
    async ({
      workflowId,
      actionsData,
    }: {
      workflowId: number;
      actionsData: UpdateWorkflowActionsRequest;
    }) => {
      return communicationWorkflowApi.updateWorkflowActions(
        workflowId,
        actionsData
      );
    },
    {
      onSuccess: (data, variable) => {
        queryClient.invalidateQueries({
          queryKey: ["ux", "uxWorkflow", "list"],
        });
      },
    }
  );
};

export const useActivateWorkflow = () => {
  const queryKey = QueryKeyFactory.module.ux.workflow.workflowTable();
  const queryClient = useQueryClient();
  return useSmartMutation(
    async ({
      workflowId,
      isActive,
    }: {
      workflowId: number;
      isActive: boolean;
    }) => {
      return communicationWorkflowApi.activateWorkflow(workflowId, isActive);
    },
    {
      onSuccess: (data, variable) => {
        queryClient.invalidateQueries({
          queryKey: ["ux", "uxWorkflow", "list"],
        });

        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.communication.workflowsAll(),
        });
      },
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "",
          description: error?.message || "Failed to save template as draft",
        });
      },
    }
  );
};

export const useCloneWorkflow = () => {
  const queryKey = QueryKeyFactory.module.ux.workflow.workflowTable();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async ({
      workflowId,
    }: {
      workflowId: number;
      cloneData?: CloneWorkflowRequest;
    }) => {
      return communicationWorkflowApi.cloneWorkflow(workflowId);
    },
    {
      onSuccess: (data, variable) => {
        queryClient.invalidateQueries({ queryKey });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.communication.workflowsDraft(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.communication.workflowsActive(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.communication.workflowsActive(),
        });
        toast({
          title: "Success",
          description: `Workflow cloned successfully!`,
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.communication.workflowsAll(),
        });
        queryClient.invalidateQueries({
          queryKey: ["ux", "uxWorkflow", "list"],
        });
      },
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "",
          description: error?.message || "Failed to save template as draft",
        });
      },
    }
  );
};

export const useWorkflowRuns = ({
  page = 1,
  limit = 10,
  search_data,
  statusDisplay,
  type,
}: {
  page?: number;
  limit?: number;
  search_data?: string;
  statusDisplay?: string;
  type?: string;
} = {}) => {
  const key = QueryKeyFactory.module.ux.workflow.workflowRuns(
    page,
    limit,
    search_data,
    statusDisplay,
    type
  );
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    key as unknown[],
    async () => {
      // Build filters object for API call
      const filters: WorkflowRunFilters = {
        page,
        limit,
        remoteUtilityId: utilityId,
      };

      if (search_data) {
        filters.searchData = search_data;
      }

      if (statusDisplay) {
        filters.statusDisplay = statusDisplay;
      }

      return communicationWorkflowApi.getWorkflowRuns(utilityId, filters);
    },
    {
      enabled: !!utilityId,
    }
  );
};
export const useWorkflowRunById = (runId: number) => {
  const key = QueryKeyFactory.module.ux.workflow.workflowRunById(runId);
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    key as unknown[],
    async () => {
      return communicationWorkflowApi.getWorkflowRunById(runId, utilityId);
    },
    {
      enabled: !!runId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useCommunicationEventTypes = () => {
  const key = QueryKeyFactory.module.ux.workflow.eventType();

  return useSmartQuery(key as unknown[], async () => {
    return communicationWorkflowApi.getEventType();
  });
};

export const useMessageTemplates = (channelType: string) => {
  const utilityId = useRemoteUtilityId();
  const key = QueryKeyFactory.module.ux.workflow.messageTemplates(channelType);

  return useSmartQuery(
    key as unknown[],
    () => communicationWorkflowApi.getMessageTemplates(utilityId, channelType),
    {
      enabled: !!channelType && !!utilityId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );
};
